// 이메일 여러 개를 칩(태그)으로 입력받는다.
//
// 왜 필요한가: 팀 초대는 보통 한 명이 아니라 여러 명을 한꺼번에 부른다. 입력칸이
// 하나면 "보내기 → 다시 입력 → 보내기"를 사람 수만큼 반복해야 한다.
//
// ⚠️ **이메일 단위로 다룰 수 있어야 한다.** 한 칸에 쉼표로 늘어놓으면 하나가 틀렸을 때
//    어느 것이 틀렸는지 알 수 없고, 그 하나만 고치기도 어렵다. 그래서 확정된 주소는
//    각각 칩이 되고, 칩마다 **수정(연필)** 과 **제거(×)** 를 갖는다.
//
// ⚠️ 형식이 잘못된 주소도 **버리지 않는다.** 조용히 사라지면 사용자는 보낸 줄 안다.
//    잘못된 칩으로 남겨 눈에 보이게 하고, 제출은 막는다.
import { useId, useRef, useState, type KeyboardEvent, type ClipboardEvent } from "react";

/** 확정된 한 칸 */
export type EmailChip = {
  /** 화면 표기·전송에 쓰는 값 (앞뒤 공백 제거, 소문자) */
  value: string;
  /** 서버가 거절한 경우 그 이유 — 제출 후 칩에 붙는다 */
  error?: string;
};

/**
 * 형식 검사.
 *
 * ⚠️ 완벽한 이메일 정규식은 존재하지 않는다(RFC 5322 는 주석·인용까지 허용한다).
 *    여기서는 **오타를 잡는 정도**만 본다 — 최종 판정은 서버와 실제 발송이 한다.
 *    너무 엄격하면 유효한 주소(예: 서브도메인·긴 TLD)를 거절해 더 나쁘다.
 */
export function isEmailLike(v: string): boolean {
  return /^[^\s@,;]+@[^\s@,;.]+(\.[^\s@,;.]+)+$/.test(v);
}

/** 붙여넣기·타이핑에서 주소를 쪼갠다 — 쉼표·세미콜론·공백·줄바꿈 모두 구분자다 */
function split(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function EmailChipsInput({
  chips,
  onChange,
  label,
  hint,
  disabled,
  max = 50,
}: {
  chips: EmailChip[];
  onChange: (next: EmailChip[]) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** 한 번에 초대할 수 있는 수의 상한 — 실수로 수백 개를 붙여넣는 것을 막는다 */
  max?: number;
}) {
  const [draft, setDraft] = useState("");
  /** 수정 중인 칩의 자리. null 이면 맨 뒤 칸에 입력하는 중이다. */
  const [editing, setEditing] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLInputElement>(null);

  const has = (v: string, exceptIndex?: number) =>
    chips.some((c, i) => c.value === v && i !== exceptIndex);

  /** 초안을 칩으로 확정한다. 이미 있는 주소는 넣지 않고 알려 준다. */
  const commit = (raw: string) => {
    const parts = split(raw);
    if (parts.length === 0) return;
    const added: EmailChip[] = [];
    const dups: string[] = [];
    for (const p of parts) {
      if (has(p) || added.some((a) => a.value === p)) {
        dups.push(p);
        continue;
      }
      if (chips.length + added.length >= max) break;
      added.push({ value: p });
    }
    const overflow = chips.length + added.length >= max && parts.length > added.length + dups.length;
    setNotice(
      [
        dups.length ? `이미 넣은 주소는 건너뜀: ${dups.join(", ")}` : null,
        overflow ? `한 번에 ${max}개까지만 넣을 수 있습니다.` : null,
      ]
        .filter(Boolean)
        .join(" · ") || null,
    );
    if (added.length) onChange([...chips, ...added]);
    setDraft("");
  };

  const remove = (index: number) => {
    onChange(chips.filter((_, i) => i !== index));
    setNotice(null);
  };

  const startEdit = (index: number) => {
    // noUncheckedIndexedAccess 를 켜 둔 프로젝트다 — 없는 자리를 열려는 호출을 막는다
    const target = chips[index];
    if (!target) return;
    setEditing(index);
    setEditDraft(target.value);
    // 렌더 후에 자리가 생기므로 다음 프레임에 초점을 옮긴다
    requestAnimationFrame(() => editRef.current?.select());
  };

  /** 수정 확정. 비우면 그 칩을 지운다 — 지우기와 수정을 따로 배우지 않아도 된다. */
  const commitEdit = () => {
    if (editing === null) return;
    const v = editDraft.trim().toLowerCase();
    const index = editing;
    setEditing(null);
    setEditDraft("");
    if (!v) return remove(index);
    if (has(v, index)) {
      setNotice(`이미 넣은 주소입니다: ${v}`);
      return remove(index);
    }
    // 수정하면 이전 제출에서 받은 오류는 지운다 — 고쳤는데 빨간 채로 남으면 혼란스럽다
    onChange(chips.map((c, i) => (i === index ? { value: v } : c)));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (!draft.trim()) return; // 빈 칸에서 Tab 은 다음 요소로 — 가두지 않는다
      e.preventDefault();
      commit(draft);
      return;
    }
    // ⚠️ 빈 칸에서 Backspace 는 **지우지 않고 마지막 칩을 수정 상태로** 되돌린다.
    //    지워 버리면 오타 하나 때문에 다시 전부 타이핑해야 한다.
    if (e.key === "Backspace" && !draft && chips.length) {
      e.preventDefault();
      startEdit(chips.length - 1);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!/[\s,;]/.test(text)) return; // 한 개만 붙여넣은 것 — 평소대로 둔다
    e.preventDefault();
    commit(draft + " " + text);
  };

  const invalidCount = chips.filter((c) => !isEmailLike(c.value)).length;

  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>

      {/* 칩과 입력칸을 **한 상자**처럼 보이게 감싼다 — 따로 보이면 칩이 입력과 무관한
          목록처럼 읽힌다. 상자를 누르면 입력칸으로 초점이 간다. */}
      <div
        className="email-chips"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) inputRef.current?.focus();
        }}
      >
        {chips.map((chip, i) =>
          editing === i ? (
            <input
              key={`edit-${i}`}
              ref={editRef}
              className="email-chips__edit"
              value={editDraft}
              disabled={disabled}
              aria-label={`${chip.value} 수정`}
              onChange={(e) => setEditDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                if (e.key === "Escape") { e.preventDefault(); setEditing(null); setEditDraft(""); }
              }}
            />
          ) : (
            <span
              key={`chip-${i}`}
              className={
                chip.error ? "email-chip email-chip--error"
                : !isEmailLike(chip.value) ? "email-chip email-chip--invalid"
                : "email-chip"
              }
              /* 서버가 거절한 이유는 title 로도 남긴다 — 목록이 길어지면 아래 요약만으로 부족하다 */
              title={chip.error ?? (isEmailLike(chip.value) ? undefined : "이메일 형식이 아닙니다")}
            >
              <span className="email-chip__text">{chip.value}</span>
              <button
                type="button"
                className="email-chip__btn"
                disabled={disabled}
                aria-label={`${chip.value} 수정`}
                onClick={() => startEdit(i)}
              >
                {/* 연필 — 글리프로 둔다. 아이콘 자산을 새로 들이지 않는다. */}
                <span aria-hidden="true">✎</span>
              </button>
              <button
                type="button"
                className="email-chip__btn"
                disabled={disabled}
                aria-label={`${chip.value} 제거`}
                onClick={() => remove(i)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </span>
          ),
        )}

        <input
          id={inputId}
          ref={inputRef}
          className="email-chips__input"
          type="email"
          inputMode="email"
          autoComplete="off"
          disabled={disabled}
          placeholder={chips.length ? "" : "예) user@domain.com"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          /* ⚠️ 초점을 잃을 때도 확정한다. 타이핑만 하고 '보내기'를 누르면
             입력칸에 남은 주소가 빠지는데, 사용자는 넣었다고 생각한다. */
          onBlur={() => commit(draft)}
        />
      </div>

      {hint && <p className="hint">{hint}</p>}
      {notice && <p className="hint email-chips__notice" role="status">{notice}</p>}
      {invalidCount > 0 && (
        <p className="hint email-chips__notice" role="alert">
          형식이 잘못된 주소 {invalidCount}개가 있습니다 — 연필을 눌러 고치거나 ×로 지워 주세요.
        </p>
      )}
    </div>
  );
}
